import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { StatsBoxComponent, StatsBoxItem } from '../../../types/builder';
import { resolveStatsBoxIcon } from '../../../utils/statsBoxIcons';

interface Props {
  component: StatsBoxComponent;
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const randInRange = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const formatNumber = (n: number) => n.toLocaleString('en-US');

export const StatsBoxRenderer: React.FC<Props> = ({ component }) => {
  const { settings } = component;
  const items = settings.items || [];

  const initial = useMemo(
    () => items.map((it) => randInRange(it.min, it.max)),
    // we intentionally re-seed when the items array identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items.length, items.map((i) => `${i.min}-${i.max}`).join('|')],
  );

  const [values, setValues] = useState<number[]>(initial);
  const valuesRef = useRef<number[]>(initial);

  useEffect(() => {
    valuesRef.current = initial;
    setValues(initial);
  }, [initial]);

  useEffect(() => {
    const interval = Math.max(200, settings.updateInterval || 1000);
    const id = window.setInterval(() => {
      const next = valuesRef.current.map((current, idx) => {
        const it = items[idx];
        if (!it) return current;
        const change = randInRange(-it.delta, it.delta);
        return clamp(current + change, it.min, it.max);
      });
      valuesRef.current = next;
      setValues(next);
    }, interval);
    return () => window.clearInterval(id);
  }, [items, settings.updateInterval]);

  const boxGradient = `linear-gradient(180deg, ${settings.boxColorTop} 0%, ${settings.boxColorBottom} 100%)`;
  const accentGradient = `linear-gradient(120deg, transparent 0%, ${settings.accentColor}55 50%, transparent 100%)`;

  return (
    <div
      style={{
        background: settings.backgroundColor || 'transparent',
        padding: `${settings.padding}px`,
      }}
    >
      <style>{`
        @keyframes sbPrizeGlow {
          0%, 100% { text-shadow: 0 0 8px var(--sb-glow), 0 0 16px var(--sb-glow); opacity: 1; }
          50% { text-shadow: 0 0 14px var(--sb-glow), 0 0 22px var(--sb-glow); opacity: .9; }
        }
      `}</style>
      <div
        style={{
          display: 'flex',
          gap: `${settings.gap}px`,
          justifyContent: 'center',
          paddingTop: `${Math.round((settings.iconSize || 52) * 0.55)}px`,
        }}
      >
        {items.map((item: StatsBoxItem, idx) => {
          const value = values[idx] ?? randInRange(item.min, item.max);
          return (
            <div
              key={item.id}
              style={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                background: boxGradient,
                borderRadius: `${settings.borderRadius}px`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '9px',
              }}
            >
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 'inherit',
                  background: accentGradient,
                  opacity: 0.35,
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  marginTop: `-${Math.round((settings.iconSize || 52) * 0.7)}px`,
                  marginBottom: '6px',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <img
                  src={resolveStatsBoxIcon(item.icon, idx)}
                  alt={item.label}
                  width={settings.iconSize}
                  height={settings.iconSize}
                  onError={(e) => {
                    const fallback = resolveStatsBoxIcon('', idx);
                    if (e.currentTarget.src !== fallback) {
                      e.currentTarget.src = fallback;
                    }
                  }}
                  style={{
                    width: `${settings.iconSize}px`,
                    height: `${settings.iconSize}px`,
                    aspectRatio: '1 / 1',
                    objectFit: 'contain',
                    filter:
                      'drop-shadow(0 12px 16px rgba(16,24,40,0.18)) drop-shadow(0 4px 6px rgba(16,24,40,0.08))',
                  }}
                />
              </div>
              <div
                style={
                  {
                    '--sb-glow': `${settings.accentColor}aa`,
                    fontSize: `${settings.valueFontSize}px`,
                    fontWeight: 700,
                    lineHeight: 1.1,
                    color: item.highlight ? settings.accentColor : settings.textColor,
                    position: 'relative',
                    zIndex: 1,
                    animation: item.highlight
                      ? 'sbPrizeGlow 1.4s ease-in-out infinite'
                      : undefined,
                    whiteSpace: 'nowrap',
                  } as React.CSSProperties
                }
              >
                {formatNumber(value)}
              </div>
              <div
                style={{
                  fontSize: `${settings.labelFontSize}px`,
                  color: settings.textColor,
                  marginTop: '4px',
                  position: 'relative',
                  zIndex: 1,
                  textAlign: 'center',
                }}
              >
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
